import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { marqueeItems } from "../data/mock";
import { subscribe } from "../lib/api";

/* Scroll reveal wrapper */
export function Reveal({ children, className = "", as: Tag = "div", delay = "", style, ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const show = () => el.classList.add("in");
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            show();
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    io.observe(el);
    // Fallback: if the element is inside the viewport but the observer has not
    // fired (throttled tabs, instant anchor jumps), reveal it anyway.
    let ticks = 0;
    const iv = setInterval(() => {
      ticks += 1;
      const r = el.getBoundingClientRect();
      if (r.top < window.innerHeight && r.bottom > 0) {
        show();
        clearInterval(iv);
      } else if (ticks > 8) {
        clearInterval(iv);
      }
    }, 400);
    return () => {
      io.disconnect();
      clearInterval(iv);
    };
  }, []);
  return (
    <Tag ref={ref} className={`reveal ${delay} ${className}`.trim()} style={style} {...rest}>
      {children}
    </Tag>
  );
}

export const Seal = ({ k, dark }) => (
  <span className="seal" style={dark ? { borderColor: "#000", color: "#000" } : undefined}>
    {k}
  </span>
);

/* Kicker row: seal + label */
export const Kick = ({ k, children, dark }) => (
  <div className="kick">
    <Seal k={k} dark={dark} />
    <span className="label mut" style={dark ? { color: "#6d6d68" } : undefined}>
      {children}
    </span>
  </div>
);

/* White ticker band */
export function Ticker() {
  const row = (
    <>
      {marqueeItems.map((m, i) => (
        <React.Fragment key={i}>
          <span>{m}</span>
          <span>/</span>
        </React.Fragment>
      ))}
    </>
  );
  return (
    <div className="ticker" aria-hidden="true">
      <div className="ttrack">
        {row}
        {row}
      </div>
    </div>
  );
}

/* Join the tribe band */
export function JoinBand() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const res = await subscribe(email, "tribe");
      toast.success(res.duplicate ? "You are already on the list." : "You are in. Welcome to the club.");
      setEmail("");
    } catch {
      toast.error("Something broke. Try again.");
    }
    setBusy(false);
  };

  return (
    <section id="join">
      <div className="wrap">
        <Kick k="組">Join Us</Kick>
        <h2 style={{ marginTop: 14 }}>
          Train with
          <br />
          <span className="ol">the tribe.</span>
        </h2>
        <p>
          First access to art drops, weekly warrior mindset dispatches, and the training culture of the club. No
          spam. No noise. Just the code.
        </p>
        <form className="jform" onSubmit={submit} aria-label="Join the tribe email signup">
          <input
            type="email"
            name="email"
            placeholder="Your email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <button type="submit" disabled={busy}>
            {busy ? "Joining..." : "Join the Club"}
          </button>
        </form>
      </div>
    </section>
  );
}

/* Product card (mockup image, art on hover) */
export function ProductCard({ p, delay = "" }) {
  return (
    <Reveal as="article" className="pcard" delay={delay}>
      <Link to={`/shop/${p.slug}`}>
        <div className="pmedia">
          <span className="pbadge">Drop {p.drop}</span>
          <img src={p.roomThumb} alt={p.altRoom} loading="lazy" />
          <img className="art" src={p.artThumb} alt={p.altArt} loading="lazy" />
        </div>
        <div className="pbody">
          <h3>{p.title}</h3>
          <p className="pm">{p.short}</p>
          <div className="prow">
            <span className="price">From ${p.sizes[0].price}</span>
            <span className="cutlink">Shop Print</span>
          </div>
        </div>
      </Link>
    </Reveal>
  );
}

/* FAQ accordion */
export function Faq({ items }) {
  const [openIdx, setOpenIdx] = useState(0);
  return (
    <div className="faqwrap">
      {items.map((f, i) => (
        <div key={i} className={`faq-item ${openIdx === i ? "open" : ""}`}>
          <button className="faq-q" onClick={() => setOpenIdx(openIdx === i ? -1 : i)} aria-expanded={openIdx === i}>
            {f.q}
            <span className="fx" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
                <path d="M12 5v14M5 12h14" strokeLinecap="round" />
              </svg>
            </span>
          </button>
          <div className="faq-a">
            <p>{f.a}</p>
          </div>
        </div>
      ))}
    </div>
  );
}
